// src/hooks/useCotizacionCustomer.ts
import { useState, useCallback } from 'react'
import { toast } from 'react-hot-toast'
import type { CotizacionData } from '../../types/cotizacionFormTypes'
import { getMeta } from '../../utils/cotizacionFormUtils'
import { findCustomer } from '../../services/cotizacionService'

interface UseCotizacionCustomerProps {
  formData: CotizacionData
  setFormData: React.Dispatch<React.SetStateAction<CotizacionData>>
  setErrors: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>
  mode: 'view' | 'edit' | 'create'
}

export function useCotizacionCustomer({
  formData,
  setFormData,
  setErrors,
  mode
}: UseCotizacionCustomerProps) {
  const [clienteEditable, setClienteEditable] = useState(false)
  const [validandoCliente, setValidandoCliente] = useState(false)

  // Función para validar cliente
  const handleValidarCliente = useCallback(async () => {
    setValidandoCliente(true)
    setErrors(prev => {
      const { validacion_cliente, ...rest } = prev
      return rest
    })
    const tipo_documento = getMeta(formData.meta_data || [], 'tipo_documento')
    const documentoValue = getMeta(formData.meta_data || [], 'documento')
    const toastId = 'validacion-cliente'
    if (!tipo_documento || !documentoValue) {
      toast.dismiss(toastId)
      setErrors(prev => ({ ...prev, validacion_cliente: 'Debe ingresar tipo de documento y documento.' }))
      setValidandoCliente(false)
      toast.error('Debe ingresar tipo de documento y documento.', { id: toastId })
      return
    }
    try {
      toast.loading('Validando cliente...', { id: toastId })
      const clienteData = await findCustomer(tipo_documento, documentoValue)
      if (clienteData) {
        toast.dismiss(toastId)
        // Obtener ids de ciudad y calcular departamento
        const idciudad_1 = clienteData.idciudad_1 || ''
        const idciudad_2 = clienteData.idciudad_2 || ''
        // Departamento: 2 primeros dígitos del id de ciudad
        const iddepto_1 = idciudad_1 ? idciudad_1.substring(0,2) : ''
        const iddepto_2 = idciudad_2 ? idciudad_2.substring(0,2) : ''
        setFormData(prev => ({
          ...prev,
          billingAddress: {
            ...prev.billingAddress,
            first_name: clienteData.first_name || '',
            last_name: clienteData.last_name || '',
            phone: clienteData.phone || '',
            address_1: clienteData.dir_1 || '',
            city: idciudad_1,
            state: iddepto_1
          },
          shippingAddress: {
            ...prev.shippingAddress,
            address_1: clienteData.dir_2 || '',
            city: idciudad_2,
            state: iddepto_2,
            phone: clienteData.phone || ''
          },
          customer: {
            ...prev.customer,
            first_name: clienteData.first_name || '',
            last_name: clienteData.last_name || '',
            email: clienteData.email || '',
            documento: clienteData.documento || clienteData.document || '',
            tipo_documento: clienteData.tipo_documento || ''
          },
          meta_data: [
            ...(Array.isArray(prev.meta_data) ? prev.meta_data.filter(m => m.key !== 'tipo_documento' && m.key !== 'documento') : []),
            { key: 'tipo_documento', value: clienteData.tipo_documento || '' },
            { key: 'documento', value: clienteData.documento || clienteData.document || '' }
          ]
        }))
        setClienteEditable(false)
        toast.success('Datos cargados correctamente.', { id: toastId })
      } else {
        toast.dismiss(toastId)
        setClienteEditable(true)
        toast('Cliente no encontrado. Puedes crearlo manualmente.', { icon: '📝', id: toastId })
      }
    } catch (error: any) {
      toast.dismiss(toastId)
      setErrors(prev => ({ ...prev, validacion_cliente: 'Error al validar el cliente.' }))
      toast.error('Error al validar el cliente.', { id: toastId })
    } finally {
      setValidandoCliente(false)
    }
  }, [formData.meta_data, setFormData, setErrors])

  return {
    clienteEditable,
    setClienteEditable,
    validandoCliente,
    handleValidarCliente
  }
}

